/**
 * Automations Table Columns Configuration
 *
 * Defines table column structure, cell renderers, and sorting logic
 * for the Automations list view table layout.
 */

import React from "react";
import { Link } from "react-router-dom";
import { StatusBadge, CategoryIcon } from "../../ui/StatusIndicators";

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Helper function to format percentage
const formatPercentage = (value) => {
  if (!value) return "-";
  return `${value}%`;
};

// Helper function to get automation category
const getAutomationCategory = (automation) => {
  const type = automation.type?.toLowerCase() || "workflow";
  const categoryMap = {
    email: "email",
    document: "document",
    data_processing: "data",
    workflow: "workflow",
    notification: "notification",
    integration: "integration",
  };
  return categoryMap[type] || "workflow";
};

const AutomationsColumns = [
  {
    key: "name",
    title: "Automation Name",
    width: "25%",
    sortable: true,
    render: (value, automation) => (
      <div className="flex items-center space-x-3">
        <CategoryIcon category={getAutomationCategory(automation)} size="sm" />
        <div>
          <Link
            to={`/automation/${automation.id}`}
            className="font-medium text-gray-900 hover:text-indigo-600"
          >
            {value || "Untitled Automation"}
          </Link>
          {automation.description && (
            <p className="text-sm text-gray-500 truncate max-w-xs">
              {automation.description}
            </p>
          )}
        </div>
      </div>
    ),
  },

  {
    key: "status",
    title: "Status",
    width: "12%",
    sortable: true,
    render: (value, automation) => (
      <StatusBadge status={value || "inactive"} type="status" />
    ),
  },

  {
    key: "type",
    title: "Type",
    width: "15%",
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-700 capitalize">
        {value?.replace("_", " ") || "Workflow"}
      </span>
    ),
  },

  {
    key: "totalRuns",
    title: "Total Runs",
    width: "10%",
    sortable: true,
    render: (value) => (
      <span className="text-sm font-medium text-gray-900">{value || 0}</span>
    ),
  },

  {
    key: "successfulRuns",
    title: "Success Rate",
    width: "12%",
    sortable: true,
    render: (value, automation) => {
      const total = automation.totalRuns || 0;
      const successful = value || 0;
      const rate = total > 0 ? Math.round((successful / total) * 100) : 0;

      const colorClass =
        rate >= 95
          ? "text-green-600"
          : rate >= 80
            ? "text-yellow-600"
            : "text-red-600";

      return (
        <span className={`text-sm font-medium ${colorClass}`}>
          {formatPercentage(rate)}
        </span>
      );
    },
  },

  {
    key: "lastRun",
    title: "Last Run",
    width: "12%",
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-700">{formatDate(value)}</span>
    ),
  },

  {
    key: "attachedToDeals",
    title: "Deals",
    width: "10%",
    sortable: false,
    render: (value, automation) => {
      const count = automation.attachedToDeals?.length || 0;
      if (count === 0) {
        return <span className="text-sm text-gray-400">-</span>;
      }

      return (
        <span className="text-sm text-gray-700">
          {count} deal{count !== 1 ? "s" : ""}
        </span>
      );
    },
  },

  {
    key: "inProgress",
    title: "In Progress",
    width: "4%",
    sortable: true,
    render: (value) => {
      if (!value || value === 0) {
        return <span className="text-sm text-gray-400">-</span>;
      }

      return (
        <div className="flex items-center">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-1"></div>
          <span className="text-sm text-blue-600 font-medium">{value}</span>
        </div>
      );
    },
  },
];

export default AutomationsColumns;
